import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUser, uploadProfileImage } from '../../services/userService';
import { getSchools } from '../../services/schoolService';
import ProfileImageUpload from '../common/ProfileImageUpload';
import DatePicker from '../common/DatePicker';
import StateCitySelect from '../org/StateCitySelect';
import { formatISODate } from '../../utils/dateUtils';

const ROLES = [
  { id: 1, name: 'SPED Director' },
  { id: 2, name: 'SPED Teacher' },
];
const TEACHER_ROLE_ID = 2;

const inputCls =
  'w-full px-3.5 py-2.5 rounded-lg bg-gray-50 dark:bg-slate-700/60 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors text-sm';

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-xl p-4 sm:p-5 space-y-4">
      <h3 className="text-gray-900 dark:text-white font-semibold text-sm">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function AddUser() {
  const navigate = useNavigate();
  const locationRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageWarning, setImageWarning] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [schools, setSchools] = useState([]);

  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    password: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zipcode: '',
    mobile: '',
    role: '',
    school_id: '',
    hire_date: formatISODate(new Date()),
  });

  useEffect(() => {
    getSchools({ page: 1, limit: 100, status: 'active' })
      .then(({ data }) => setSchools(data.data?.schools || []))
      .catch(() => {});
  }, []);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'role' && parseInt(value, 10) !== TEACHER_ROLE_ID) {
        next.school_id = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.role) {
      setError('Role is required.');
      return;
    }
    if (parseInt(form.role, 10) === TEACHER_ROLE_ID && !form.school_id) {
      setError('School is required for SPED Teachers.');
      return;
    }
    if ((form.state || form.city) && !locationRef.current?.validate()) {
      setError('Please select state and city from the dropdown.');
      return;
    }
    setLoading(true);

    const payload = { ...form };
    if (payload.zipcode) payload.zipcode = parseInt(payload.zipcode);
    if (payload.mobile) payload.mobile = parseInt(payload.mobile);
    if (payload.role) payload.role = parseInt(payload.role);
    if (payload.school_id) payload.school_id = parseInt(payload.school_id, 10);
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '' || payload[k] === null) delete payload[k];
    });

    try {
      const { data } = await createUser(payload);
      const newUserId = data.data?.id;

      if (profileImage && newUserId) {
        try {
          await uploadProfileImage(newUserId, profileImage);
        } catch {
          setImageWarning('User created but profile image upload failed. You can add it later from the edit page.');
          setTimeout(() => navigate('/users'), 2500);
          return;
        }
      }
      navigate('/users');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full min-w-0 max-w-[min(100%,64rem)] space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/users"
          className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Add New User</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm">Register a new SPED staff member</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {imageWarning && (
          <div className="px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-sm">
            {imageWarning}
          </div>
        )}

        <Section title="Profile Photo">
          <ProfileImageUpload
            initials={`${form.first_name?.[0] || ''}${form.last_name?.[0] || ''}`.toUpperCase() || '??'}
            previewFile={profileImage}
            onFileSelect={setProfileImage}
            onRemove={() => setProfileImage(null)}
          />
        </Section>

        <Section title="Basic Information">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="First Name *">
              <input
                type="text"
                required
                value={form.first_name}
                onChange={set('first_name')}
                placeholder="John"
                className={inputCls}
              />
            </Field>
            <Field label="Middle Name">
              <input
                type="text"
                value={form.middle_name}
                onChange={set('middle_name')}
                placeholder="K"
                className={inputCls}
              />
            </Field>
            <Field label="Last Name *">
              <input
                type="text"
                required
                value={form.last_name}
                onChange={set('last_name')}
                placeholder="Doe"
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email *">
              <input
                type="email"
                required
                value={form.email}
                onChange={set('email')}
                placeholder="john@example.com"
                className={inputCls}
              />
            </Field>
            <Field label="Password *">
              <input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={set('password')}
                placeholder="••••••••"
                className={inputCls}
              />
            </Field>
            <Field label="Mobile">
              <input
                type="text"
                value={form.mobile}
                onChange={set('mobile')}
                placeholder="8015551234"
                className={inputCls}
              />
            </Field>
          </div>
        </Section>

        <Section title="Role & Status">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Role *">
              <select required value={form.role} onChange={set('role')} className={inputCls}>
                <option value="">Select role</option>
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
            {parseInt(form.role, 10) === TEACHER_ROLE_ID && (
              <Field label="School *">
                <select required value={form.school_id} onChange={set('school_id')} className={inputCls}>
                  <option value="">Select school</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>{s.school_name}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Hire Date">
              <DatePicker
                value={form.hire_date}
                onChange={(v) => setForm((prev) => ({ ...prev, hire_date: v }))}
                outputFormat="YYYY-MM-DD"
                className={inputCls}
                aria-label="Hire Date"
              />
            </Field>
          </div>
        </Section>

        <Section title="Address (Optional)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Address Line 1">
              <input
                type="text"
                value={form.address_line1}
                onChange={set('address_line1')}
                placeholder="123 Main St"
                className={inputCls}
              />
            </Field>
            <Field label="Address Line 2">
              <input
                type="text"
                value={form.address_line2}
                onChange={set('address_line2')}
                placeholder="Suite 200"
                className={inputCls}
              />
            </Field>
            <StateCitySelect
              ref={locationRef}
              inputNamePrefix="athenian-user"
              stateCode={form.state}
              city={form.city}
              onStateChange={(value) => setForm((prev) => ({ ...prev, state: value, city: '' }))}
              onCityChange={(value) => setForm((prev) => ({ ...prev, city: value }))}
            />
            <Field label="Zipcode">
              <input
                type="text"
                value={form.zipcode}
                onChange={set('zipcode')}
                placeholder="84101"
                className={inputCls}
              />
            </Field>
          </div>
        </Section>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-400 dark:hover:border-slate-600 text-sm font-medium transition-colors text-center"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20 text-center"
          >
            {loading ? 'Saving...' : 'Create User'}
          </button>
        </div>
      </form>
    </div>
  );
}